import { useTheme } from '../../hooks/useTheme';
import { Tile, Button, Pill, Table, Row, Cell, Pagination, Dropdown, MenuItem } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Enm, EnvironmentCategory } from './EnmInterfaces';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import api from '../../api/axiosInstance';
import { AxiosError } from 'axios';
import LinkEiapClustersDialog from './LinkEiapClustersDialog';

/**
 * ENMs (Ericsson Network Managers) page
 *
 * ENM is the Ericsson Network Manager that provides network management capabilities
 *
 * Key features:
 * - Display ENM instances in paginated table
 * - Show status badges (connected, disconnected, error)
 * - Show environment category
 * - Create/Edit/Delete ENM instances via row actions
 * - Link/unlink EIAP Clusters to ENMs
 * - Filter by environment category
 */
function Enms() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  // State for ENMs from backend
  const [enms, setEnms] = useState<Enm[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Environment category filtering
  const categoryPills: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];
  const [filteredCategories, setFilteredCategories] = useState<EnvironmentCategory[]>(categoryPills);

  // Link EIAP Clusters Dialog state
  const [linkClustersDialogOpen, setLinkClustersDialogOpen] = useState(false);
  const [selectedEnmId, setSelectedEnmId] = useState<string>('');

  // Fetch ENMs from backend on component mount
  useEffect(() => {
    fetchEnms();
  }, []);

  const fetchEnms = async () => {
    try {
      showLoader();
      const response = await api.get('/api/enm');
      setEnms(response.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error - ENMs',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching ENMs.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
    setTimeout(() => {
      hideLoader();
    }, 500);
  }
  };

  const handleCreateEnm = () => {
    navigate('/integrations/enms/new');
  };

  const handleEditEnm = (enmId: string) => {
    navigate(`/integrations/enms/edit/${enmId}`);
  };

  const handleDeleteEnm = async (enmId: string) => {
    try {
      showLoader();
      await api.delete(`/api/enm/${enmId}`);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: 'ENM deleted successfully!',
          timestamp: new Date(),
          color: 'green',
        },
      ]);
      // Refresh the list after deletion
      await fetchEnms();
      hideLoader();
    } catch (error) {
      hideLoader();
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while deleting ENM.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleLinkEiapClusters = (enmId: string) => {
    setSelectedEnmId(enmId);
    setLinkClustersDialogOpen(true);
  };

  const handleSaveLinkClusters = async (enmId: string, linkedClusterIds: string[]) => {
    try {
      showLoader();
      await api.put(`/api/enm/${enmId}/link-eiap-clusters`, { clusterIds: linkedClusterIds });
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `EIAP cluster links updated successfully for ENM!`,
          timestamp: new Date(),
          color: 'green',
        },
      ]);
      // Refresh the list after linking
      await fetchEnms();
      setLinkClustersDialogOpen(false);
      hideLoader();
    } catch (error) {
      hideLoader();
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while linking EIAP clusters.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleCloseLinkClustersDialog = () => {
    setLinkClustersDialogOpen(false);
    setSelectedEnmId('');
  };

  const getStatusColor = (status: Enm['status']) => {
    switch (status) {
      case 'connected':
        return '#28a745'; // Green
      case 'disconnected':
        return '#6c757d'; // Gray
      case 'error':
        return '#dc3545'; // Red
      default:
        return '#6c757d';
    }
  };

  const getCategoryTagClass = (category: Enm['category']) => {
    switch (category) {
      case 'Production':
        return 'red';
      case 'Staging':
        return 'yellow';
      case 'Development':
        return 'blue';
      case 'Testing':
        return 'green';
      default:
        return 'purple';
    }
  };

  // Filter and paginate data
  const filteredEnms = useMemo(() => {
    return enms.filter((enm) => filteredCategories.includes(enm.category));
  }, [enms, filteredCategories]);

  const paginatedEnms = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredEnms.slice(startIndex, endIndex);
  }, [filteredEnms, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredEnms.length / itemsPerPage);

  // Table columns configuration
  const columns = [
    { title: 'Name', attribute: 'name', width: '20%' },
    { title: 'Category', attribute: 'category', width: '12%' },
    { title: 'Status', attribute: 'status', width: '10%' },
    { title: 'URL', attribute: 'url', width: '20%' },
    { title: 'SFTP', attribute: 'sftp', width: '15%' },
    { title: 'Linked Clusters', attribute: 'clusters', width: '8%' },
  ];

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper component={Tile} title="ENMs" id="enms-tile">
            {/* Header with Create Button */}
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <ReactEDSWrapper component={Button} primary onClick={handleCreateEnm}>
                <i className="icon icon-plus" /> Create New ENM
              </ReactEDSWrapper>
            </div>
            {/* Category Filter Pills */}
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {categoryPills.map((category) => (
                <ReactEDSWrapper
                  key={category}
                  component={Pill}
                  toggle
                  unselected={!filteredCategories.includes(category)}
                  onToggle={(unselected) => {
                    if (unselected) {
                      setFilteredCategories([...filteredCategories, category]);
                    } else if (filteredCategories.length === categoryPills.length) {
                      setFilteredCategories([category]);
                    } else if (filteredCategories.length === 1) {
                      setFilteredCategories(categoryPills);
                    } else {
                      setFilteredCategories(filteredCategories.filter((cat) => cat !== category));
                    }
                    setCurrentPage(1); // Reset to first page when filtering
                  }}
                >
                  {category}
                </ReactEDSWrapper>
              ))}
              <div style={{ color: '#6a6a6a' }}>
                {filteredEnms.length} ENM{filteredEnms.length !== 1 ? 's' : ''}
              </div>
              {filteredCategories.length < categoryPills.length && (
                <a
                  href="javascript:void(0);"
                  onClick={() => {
                    setFilteredCategories(categoryPills);
                    setCurrentPage(1);
                  }}
                  style={{ marginLeft: '8px', fontSize: '14px' }}
                >
                  Select all
                </a>
              )}
            </div>

            {/* ENMs Table */}
            <ReactEDSWrapper component={Table} columns={columns} rowActions>
              {paginatedEnms.map((enm) => (
                <ReactEDSWrapper
                  key={enm.id}
                  component={Row}
                  id={enm.id}
                  actions={[
                    <ReactEDSWrapper component={Dropdown} more>
                      <ReactEDSWrapper component={MenuItem} onClick={() => enm.id && handleLinkEiapClusters(enm.id)}>
                        <i className="icon icon-network" style={{ marginRight: '8px' }} />
                        Link EIAP Cluster
                      </ReactEDSWrapper>
                      <ReactEDSWrapper component={MenuItem} onClick={() => enm.id && handleEditEnm(enm.id)}>
                        <i className="icon icon-edit" style={{ marginRight: '8px' }} />
                        Edit
                      </ReactEDSWrapper>
                      <ReactEDSWrapper component={MenuItem} onClick={() => enm.id && handleDeleteEnm(enm.id)}>
                        <i className="icon icon-trashcan" style={{ marginRight: '8px' }} />
                        Delete
                      </ReactEDSWrapper>
                    </ReactEDSWrapper>
                  ]}
                >
                  {/* Name */}
                  <ReactEDSWrapper component={Cell}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{enm.name}</div>
                      <div style={{ fontSize: '12px', color: '#6a6a6a', marginTop: '4px' }}>{enm.description}</div>
                    </div>
                  </ReactEDSWrapper>

                  {/* Category */}
                  <ReactEDSWrapper component={Cell}>
                    <kbd className={`tag large ${getCategoryTagClass(enm.category)}`}>{enm.category}</kbd>
                  </ReactEDSWrapper>

                  {/* Status */}
                  <ReactEDSWrapper component={Cell}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <i
                        className="icon icon-circle"
                        style={{
                          fontSize: '8px',
                          color: getStatusColor(enm.status),
                          marginRight: '6px',
                        }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{enm.status}</span>
                    </div>
                  </ReactEDSWrapper>

                  {/* URL */}
                  <ReactEDSWrapper component={Cell}>
                    <a href={enm.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
                      {enm.url}
                    </a>
                  </ReactEDSWrapper>

                  {/* SFTP */}
                  <ReactEDSWrapper component={Cell}>
                    <div style={{ fontSize: '12px' }}>
                      <div style={{ fontFamily: 'monospace' }}>{enm.sftpIp}</div>
                      <div style={{ color: '#6a6a6a', marginTop: '2px' }}>{enm.sftpPath}</div>
                    </div>
                  </ReactEDSWrapper>

                  {/* Linked Clusters */}
                  <ReactEDSWrapper component={Cell}>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ fontWeight: enm.linkedEiapClusters?.length > 0 ? '500' : 'normal', color: enm.linkedEiapClusters?.length > 0 ? 'inherit' : '#6a6a6a' }}>
                        {enm.linkedEiapClusters?.length || 0}
                      </span>
                    </div>
                  </ReactEDSWrapper>
                </ReactEDSWrapper>
              ))}
            </ReactEDSWrapper>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                <ReactEDSWrapper
                  component={Pagination}
                  currentPage={currentPage}
                  numPages={totalPages}
                  onPageChange={(page: number) => setCurrentPage(page)}
                />
              </div>
            )}

            {/* No results message */}
            {filteredEnms.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6a6a6a' }}>
                <i className="icon icon-info" style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }} />
                <p>No ENMs found matching the selected categories.</p>
              </div>
            )}
          </ReactEDSWrapper>
        </div>
      </div>

      {/* Link EIAP Clusters Dialog */}
      <LinkEiapClustersDialog
        open={linkClustersDialogOpen}
        enmId={selectedEnmId}
        onSave={handleSaveLinkClusters}
        onClose={handleCloseLinkClustersDialog}
      />
    </div>
  );
}

export default Enms;
